/* eslint-disable react/prop-types */
import React, {
  useEffect, useMemo, useState,
} from 'react';
import {
  Button, Card, Checkbox, Empty, Input, message, Modal, Radio, Row, Spin, Table,
} from 'antd';
import {
  CloseOutlined, FilePdfOutlined, IdcardOutlined, SearchOutlined,
} from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { getEmpleadosvistaOptimized } from 'api/estructuraOrganizacional/empleados';
import { postObtenerReporte } from 'api/reporteador';

export const impresionDeGafetesPermission = {
  permissionModel: 'empleado',
  catalogsModels: [
    'estadodelempleado',
  ],
};

const ESTADOS_IMPRIMIBLES_IDS = [2, 6];

// OID del reporte en el reporteador para cada formato de gafete.
const REPORTES = {
  bond: { id: 4001, label: 'Bond (Fiscalía Ambiental / Inspección y Vigilancia)' },
  pvc: { id: 4002, label: 'Tarjeta PVC (resto del personal)' },
};

const PAGE_SIZE = 10;
const BASE_COLUMNS = [
  {
    title: 'Número de Empleado', dataIndex: 'numero_de_empleado', key: 'numero_de_empleado', width: 180,
  },
  {
    title: 'Nombre', dataIndex: 'nombre', key: 'nombre', width: 200,
  },
  {
    title: 'Apellido Paterno', dataIndex: 'apellido_paterno', key: 'apellido_paterno', width: 200,
  },
  {
    title: 'Apellido Materno', dataIndex: 'apellido_materno', key: 'apellido_materno', width: 200,
  },
  {
    title: 'Puesto', dataIndex: 'puesto_clave_y_descripcion', key: 'puesto_clave_y_descripcion', width: 220,
  },
  {
    title: 'Área', dataIndex: 'directorio_area_clave_y_nombre', key: 'directorio_area_clave_y_nombre', width: 250,
  },
  {
    title: 'Estado', dataIndex: 'estado_del_empleado', key: 'estado_del_empleado', width: 140,
  },
];

const ImpresionDeGafetes = () => {
  const [loading, setLoading] = useState(false);
  const [empleados, setEmpleados] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [formato, setFormato] = useState('bond');
  const [base64, setBase64] = useState(null);
  const [visiblePreview, setVisiblePreview] = useState(false);
  const [filters, setFilters] = useState({});
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const cargarEmpleados = async () => {
      setLoading(true);
      const data = await getEmpleadosvistaOptimized();
      const lista = formatReceived(data);
      const activos = lista.filter(
        (e) => ESTADOS_IMPRIMIBLES_IDS.includes(Number(e.estado_del_empleado_id)),
      );
      setEmpleados(activos);
      setLoading(false);
    };
    cargarEmpleados();
  }, []);

  const toggleEmpleado = (record) => {
    setSelectedRowKeys((prev) => (
      prev.includes(record.id) ? prev.filter((id) => id !== record.id) : [...prev, record.id]
    ));
  };

  const setFilter = (dataIndex, value) => {
    setFilters((prev) => ({ ...prev, [dataIndex]: value }));
    setPage(1);
  };

  const empleadosFiltrados = useMemo(() => {
    const entries = Object.entries(filters).filter(([, value]) => !!value);
    if (!entries.length) return empleados;
    return empleados.filter((row) => entries.every(([dataIndex, value]) => (
      String(row[dataIndex] ?? '').toLowerCase().includes(value.toLowerCase())
    )));
  }, [empleados, filters]);

  const tableColumns = useMemo(() => BASE_COLUMNS.map((col) => ({
    ...col,
    title: (
      <>
        <div>{col.title}</div>
        {visibleSearch && (
          <Input
            size="small"
            value={filters[col.dataIndex] || ''}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setFilter(col.dataIndex, e.target.value)}
            suffix={filters[col.dataIndex]
              ? <CloseOutlined onClick={() => setFilter(col.dataIndex, '')} />
              : <SearchOutlined />}
          />
        )}
      </>
    ),
  })), [filters, visibleSearch]);

  // Solo los empleados de la página actual -- el checkbox de "seleccionar todos" del
  // encabezado selecciona/deselecciona nada más estos (no los de las demás páginas), para
  // poder ir acumulando selección página por página en vez de un "todos" absoluto.
  const empleadosPaginaActual = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return empleadosFiltrados.slice(start, start + PAGE_SIZE);
  }, [empleadosFiltrados, page]);

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    columnTitle: (
      <Checkbox
        checked={empleadosPaginaActual.length > 0
          && empleadosPaginaActual.every((row) => selectedRowKeys.includes(row.id))}
        indeterminate={
          empleadosPaginaActual.some((row) => selectedRowKeys.includes(row.id))
          && !empleadosPaginaActual.every((row) => selectedRowKeys.includes(row.id))
        }
        onChange={(e) => {
          const idsPagina = empleadosPaginaActual.map((row) => row.id);
          setSelectedRowKeys((prev) => (
            e.target.checked
              ? [...prev, ...idsPagina.filter((id) => !prev.includes(id))]
              : prev.filter((id) => !idsPagina.includes(id))
          ));
        }}
      />
    ),
    onSelect: (record, selected) => {
      setSelectedRowKeys((prev) => (
        selected ? [...prev, record.id] : prev.filter((id) => id !== record.id)
      ));
    },
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const onClickPreview = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Selecciona al menos un empleado.');
      return;
    }
    setLoading(true);
    try {
      const values = {
        ID: REPORTES[formato].id,
        Parametros: [
          {
            Nombre: 'IdsEmpleados',
            TipoParametro: 'EnteroLista',
            Valores: selectedRowKeys.map((id) => ({ Value: id })),
          },
        ],
      };
      const file = await postObtenerReporte(values);
      if (file) {
        setBase64(file);
        setVisiblePreview(true);
      } else {
        message.error('Error al generar los gafetes. Inténtalo de nuevo más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title={(
        <span>
          <IdcardOutlined style={{ marginRight: 8 }} />
          Impresión de Gafetes
        </span>
      )}
      extra={(
        <Radio.Group value={formato} onChange={(e) => setFormato(e.target.value)}>
          <Radio.Button value="bond">{REPORTES.bond.label}</Radio.Button>
          <Radio.Button value="pvc">{REPORTES.pvc.label}</Radio.Button>
        </Radio.Group>
      )}
    >
      <Spin tip="Cargando..." spinning={loading}>
        <Row justify="end" style={{ marginBottom: 8 }}>
          <Button
            type="link"
            icon={<SearchOutlined />}
            onClick={() => setVisibleSearch((prev) => !prev)}
          >
            Búsqueda
          </Button>
          <Button
            type="link"
            icon={<FilePdfOutlined />}
            disabled={!selectedRowKeys.length}
            onClick={onClickPreview}
          >
            Vista Previa
          </Button>
        </Row>
        <Table
          size="small"
          rowKey="id"
          columns={tableColumns}
          dataSource={empleadosFiltrados}
          rowSelection={rowSelection}
          onRow={(record) => ({ onClick: () => toggleEmpleado(record) })}
          pagination={{
            pageSize: PAGE_SIZE,
            position: ['bottomLeft'],
            current: page,
            onChange: setPage,
          }}
          className="custom-table selectable"
        />
      </Spin>

      <Modal
        title="Vista Previa de Gafetes"
        open={visiblePreview}
        onCancel={() => setVisiblePreview(false)}
        footer={null}
        width="80%"
        style={{ top: 20 }}
        destroyOnClose
      >
        <div style={{ height: '75vh', backgroundColor: '#f5f5f5', padding: '10px' }}>
          {base64 ? (
            <iframe
              src={base64}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: 'white',
                borderRadius: '4px',
              }}
              title="Vista Previa de Gafetes"
            />
          ) : (
            <Empty description="No hay gafetes para mostrar." style={{ marginTop: '10%' }} />
          )}
        </div>
      </Modal>
    </Card>
  );
};

export default ImpresionDeGafetes;
